import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useNavigate } from "react-router-dom";
import {
  HardHat, Wallet, RefreshCcw, TrendingUp, IndianRupee,
  Users, ClipboardCheck, Construction, CheckCircle2, Clock,
} from "lucide-react";
import toast from "react-hot-toast";
import { format } from "date-fns";

// Extracted Sub-Components
import MiniStat from "../../component/dashboard/MiniStat";
import ActionBtn from "../../component/dashboard/ActionBtn";
import DashboardChartsGrid from "../../component/dashboard/DashboardChartsGrid";
import FilterModal from "../../component/dashboard/FilterModal";
import LoadingScreen from "../../component/dashboard/LoadingScreen";

const HPCL_COLORS = ["#0054A6", "#EE3124", "#FFB81C", "#10b981", "#6366f1"];

export default function ConstructionDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [personalFlowData, setPersonalFlowData] = useState([]);
  const [personalCategoryDist, setPersonalCategoryDist] = useState([]);
  const [supervisorFlowData, setSupervisorFlowData] = useState([]);
  const [supervisorCategoryDist, setSupervisorCategoryDist] = useState([]);
  const [siteWiseSpend, setSiteWiseSpend] = useState([]);
  const [laborTrendData, setLaborTrendData] = useState([]);
  
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterType, setFilterType] = useState("FULL");
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  const [stats, setStats] = useState({
    outstandingInvoices: 0,
    settledAmount: 0,
    totalExpenses: 0,
    totalCash: 0,
    attendanceRate: 0,
    todayLogs: 0,
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // 1. Build Isolated Filter Queries
      let logsQuery = supabase.from("daily_logs").select("*");
      let personalQuery = supabase.from("personal_expenses").select("*");
      let supervisorLogsQuery = supabase.from("supervisor_logs").select("*");
      let invoicesQuery = supabase.from("invoices").select("*");
      let attendanceQuery = supabase.from("attendance").select("*");

      if (filterType === "SINGLE") {
        logsQuery = logsQuery.eq("date", startDate);
        personalQuery = personalQuery.eq("date", startDate);
        supervisorLogsQuery = supervisorLogsQuery.eq("date", startDate);
        invoicesQuery = invoicesQuery.eq("date", startDate);
        attendanceQuery = attendanceQuery.eq("date", startDate);
      } else if (filterType === "RANGE") {
        logsQuery = logsQuery.gte("date", startDate).lte("date", endDate);
        personalQuery = personalQuery.gte("date", startDate).lte("date", endDate);
        supervisorLogsQuery = supervisorLogsQuery.gte("date", startDate).lte("date", endDate);
        invoicesQuery = invoicesQuery.gte("date", startDate).lte("date", endDate);
        attendanceQuery = attendanceQuery.gte("date", startDate).lte("date", endDate);
      }

      // 2. Resolve Promises concurrently
      const [
        projectsRes,
        sitesRes,
        supervisorsRes,
        settingsRes,
        filteredLogsRes,
        filteredPersonalRes,
        filteredSupervisorLogsRes,
        filteredInvoicesRes,
        filteredAttendanceRes,
      ] = await Promise.all([
        supabase.from("projects").select("*"),
        supabase.from("sites").select("*"),
        supabase.from("supervisors").select("*"),
        supabase.from("settings").select("total_cash").eq("id", 1).maybeSingle(),
        logsQuery.order("date", { ascending: true }),
        personalQuery,
        supervisorLogsQuery.order("date", { ascending: true }),
        invoicesQuery,
        attendanceQuery,
      ]);

      const sites = sitesRes.data || [];
      const supervisors = supervisorsRes.data || [];
      const settingsData = settingsRes.data || { total_cash: 0 };
      
      const dailyLogs = filteredLogsRes.data || [];
      const personalData = filteredPersonalRes.data || [];
      const supervisorLogs = filteredSupervisorLogsRes.data || [];
      const invoicesData = filteredInvoicesRes.data || [];
      const attendanceData = filteredAttendanceRes.data || [];

      // --- PERSONAL LEDGER REDUCTION ---
      const categoryMap = personalData.reduce((acc, item) => {
        if (item.type === "expense") {
          acc[item.category] = (acc[item.category] || 0) + Number(item.amount);
        }
        return acc;
      }, {});
      setPersonalCategoryDist(Object.keys(categoryMap).map(cat => ({ name: cat, value: categoryMap[cat] })));

      const dailyFlow = personalData.reduce((acc, item) => {
        let timeKey = filterType === "SINGLE" && item.created_at ? format(new Date(item.created_at), "HH:mm") : item.date;
        if (!acc[timeKey]) acc[timeKey] = { name: timeKey, income: 0, expense: 0 };
        acc[timeKey][item.type] += Number(item.amount);
        return acc;
      }, {});
      setPersonalFlowData(Object.values(dailyFlow).sort((a, b) => filterType === "SINGLE" ? a.name.localeCompare(b.name) : new Date(a.name) - new Date(b.name)));

      // --- FIXED: ACCURATE SUPERVISOR BALANCES ---
      const supCategoryMap = supervisorLogs.reduce((acc, log) => {
        acc["Food"] = (acc["Food"] || 0) + Number(log.food_expenses || 0);
        acc["Room"] = (acc["Room"] || 0) + Number(log.room_expenses || 0);
        acc["Travel"] = (acc["Travel"] || 0) + Number(log.travel_expenses || 0);
        acc["Other"] = (acc["Other"] || 0) + Number(log.other_expenses || 0);
        acc["Advance Taken"] = (acc["Advance Taken"] || 0) + Number(log.advance_taken || 0);
        acc["Salary Credited"] = (acc["Salary Credited"] || 0) + Number(log.salary_credited || 0);
        return acc;
      }, {});
      setSupervisorCategoryDist(Object.keys(supCategoryMap).map(cat => ({ name: cat, value: supCategoryMap[cat] })).filter(i => i.value > 0));

      const supDailyFlow = supervisorLogs.reduce((acc, log) => {
        let timeKey = filterType === "SINGLE" && log.created_at ? format(new Date(log.created_at), "HH:mm") : log.date;
        const logCost = Number(log.food_expenses || 0) + Number(log.room_expenses || 0) + 
                       Number(log.travel_expenses || 0) + Number(log.other_expenses || 0) + 
                       Number(log.advance_taken || 0) + Number(log.salary_credited || 0);

        if (!acc[timeKey]) acc[timeKey] = { name: timeKey, amount: 0 };
        acc[timeKey].amount += logCost;
        return acc;
      }, {});
      setSupervisorFlowData(Object.values(supDailyFlow).sort((a, b) => filterType === "SINGLE" ? a.name.localeCompare(b.name) : new Date(a.name) - new Date(b.name)));

      // --- EXTRA MAPPED ANALYTICS: DAILY LABOR ATTENDANCE ---
      const laborFlow = dailyLogs.reduce((acc, log) => {
        const key = log.date;
        if (!acc[key]) acc[key] = { name: key, labours: 0 };
        acc[key].labours += Number(log.labour_count || 0);
        return acc;
      }, {});
      setLaborTrendData(Object.values(laborFlow).sort((a, b) => new Date(a.name) - new Date(b.name)));

      // --- CALCULATE GLOBAL BALANCES SAFELY ---
      const outstanding = invoicesData.reduce((sum, inv) => inv.status === "pending" ? sum + Number(inv.base_amount || 0) : sum, 0);
      const settledAmount = invoicesData.reduce((sum, inv) => (inv.status === "done" || inv.status === "settled") ? sum + Number(inv.settled_amount || 0) : sum, 0);

      let attendanceRate = 0;
      if (supervisors.length > 0) {
        if (filterType === "SINGLE" || filterType === "RANGE") {
          attendanceRate = ((attendanceData.length / supervisors.length) * 100).toFixed(0);
        } else {
          const todayStr = format(new Date(), "yyyy-MM-dd");
          const totalTodayAttendance = attendanceData.filter((a) => a.date === todayStr).length;
          attendanceRate = ((totalTodayAttendance / supervisors.length) * 100).toFixed(0);
        }
      }

      const expenseFromLogs = dailyLogs.reduce((sum, log) => sum + Number(log.total_expenses || 0), 0);
      const personalExpensesTotal = personalData.reduce((sum, item) => item.type === "expense" ? sum + Number(item.amount || 0) : sum, 0);
      
      // Fixed lookup mapping reference loop pointing to correct filtered array
      const expenseFromSupervisorLogs = supervisorLogs.reduce((sum, log) => {
        return (
          sum +
          Number(log.food_expenses || 0) + Number(log.room_expenses || 0) +
          Number(log.travel_expenses || 0) + Number(log.other_expenses || 0) +
          Number(log.advance_taken || 0) + Number(log.salary_credited || 0)
        );
      }, 0);

      const totalExpenses = expenseFromLogs + expenseFromSupervisorLogs + personalExpensesTotal;
      const initialCash = Number(settingsData.total_cash || 0);
      const remainingCash = initialCash - totalExpenses;

      const siteSpendMap = dailyLogs.reduce((acc, log) => {
        acc[log.site_id] = (acc[log.site_id] || 0) + Number(log.total_expenses || 0);
        return acc;
      }, {});

      setSiteWiseSpend(
        sites.map((site) => ({
          name: site.site_name,
          spend: siteSpendMap[site.id] || 0,
        })).sort((a, b) => b.spend - a.spend)
      );

      setStats({
        outstandingInvoices: outstanding,
        settledAmount: settledAmount,
        totalExpenses: totalExpenses,
        totalCash: remainingCash >= 0 ? remainingCash : 0,
        attendanceRate: attendanceRate,
        todayLogs: dailyLogs.length,
      });

    } catch (err) {
      console.error("Dashboard error:", err);
      toast.error("Sync Failed");
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-[#F1F5F9] pb-12">
      {/* HEADER BAR SECTION */}
      <div className="bg-white border-b border-slate-200 px-4 py-4 lg:px-8 top-0 z-50 shadow-sm">
        <div className="max-w-[1600px] mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 group cursor-pointer">
              <div className="relative w-10 h-10 bg-[#0054A6] rounded-xl flex items-center justify-center text-white shadow-lg rotate-3 group-hover:rotate-0 transition-transform">
                <Construction size={22} strokeWidth={2.5} />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-red-600 rounded-md border-2 border-white" />
              </div>
              <div>
                <h1 className="text-lg font-black text-slate-900 leading-none tracking-tight">
                  PROTECH <span className="text-[#0054A6]">CONSTRUCTION</span>
                </h1>
                <p className="text-[10px] font-bold text-slate-400 tracking-[0.2em] uppercase">Building Excellence</p>
              </div>
            </div>
            <span className="hidden md:block text-slate-200 font-light mx-2">|</span>
            <span className="hidden md:inline-flex items-center text-[10px] bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-black animate-pulse uppercase">Live System</span>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <button onClick={fetchDashboardData} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500">
              <RefreshCcw size={18} />
            </button>
            <button onClick={() => setShowFilterModal(true)} className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-xl text-[10px] font-black text-slate-600 hover:bg-slate-50">
              <Clock size={14} /> FILTER BY DATE
            </button>
          </div>
        </div>
      </div>

      {/* BODY VIEWPORT HOOKS */}
      <div className="max-w-[1600px] mx-auto p-4 lg:p-8 space-y-6">
        
        {/* TOP KPI SUMMARY CARD DECK */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
          <MiniStat icon={<IndianRupee />} label={filterType === "SINGLE" ? "Outstanding (Day)" : "Outstanding"} value={`₹${stats.outstandingInvoices.toLocaleString()}`} color="text-red-600" />
          <MiniStat icon={<Construction />} label={filterType === "SINGLE" ? "Settled (Day)" : "Settled Amount"} value={`₹${stats.settledAmount.toLocaleString()}`} color="text-blue-600" />
          <MiniStat icon={<Wallet />} label="Total Cash" value={`₹${stats.totalCash.toLocaleString()}`} color="text-emerald-600" />
          <MiniStat icon={<TrendingUp />} label="Total Expenses" value={`₹${stats.totalExpenses.toLocaleString()}`} color="text-amber-600" />
          <MiniStat icon={<Users />} label={filterType === "SINGLE" ? "Day Attendance" : "Attendance Rate"} value={`${stats.attendanceRate}%`} color="text-blue-600" />
          <MiniStat icon={<Clock />} label={filterType === "SINGLE" ? "Day's Logs" : "Logs Count"} value={stats.todayLogs} color="text-purple-600" />
        </div>

        {/* QUICK ACTIONS NAVIGATION BUTTON PANEL */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <ActionBtn icon={<Users size={24} />} label="Staff Management" color="bg-blue-600" onClick={() => navigate("/supervisor")} />
          <ActionBtn icon={<HardHat size={24} />} label="Site Inventory" color="bg-slate-900" onClick={() => navigate("/sites")} />
          <ActionBtn icon={<ClipboardCheck size={24} />} label="Billing & Invoices" color="bg-red-600" onClick={() => navigate("/invoices")} />
          <ActionBtn icon={<CheckCircle2 size={24} />} label="Expense Reports" color="bg-emerald-600" onClick={() => navigate("/personal-expenses")} />
        </div>

        {/* 4-COLUMN SYMMETRICAL GRAPH LABELS GRID ZONE */}
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col gap-1 px-2 mb-2">
            <h2 className="text-sm font-black text-slate-800 uppercase tracking-widest">Business Intelligence Summary</h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase">Chronological progression trends grid sheet</p>
          </div>
          <DashboardChartsGrid 
            siteWiseSpend={siteWiseSpend}
            personalFlowData={personalFlowData}
            supervisorFlowData={supervisorFlowData}
            laborTrendData={laborTrendData}
            filterType={filterType}
          />
        </div>

      </div>

      {/* FILTER CONTROLLERS OVERLAY SELECTION LAYER */}
      <FilterModal
        show={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filterType={filterType}
        setFilterType={setFilterType}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onApply={() => {
          fetchDashboardData();
          setShowFilterModal(false);
        }}
      />
    </div>
  );
}